import { useCallback, useEffect, useState } from "react";
import { Plus, Trash2, Mail, CheckCircle2, Circle, UserPlus } from "lucide-react";
import { api, formatApiError } from "../api/client";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

const RACI = [
  { value: "responsible", label: "R · lo hace" },
  { value: "accountable", label: "A · responde de que se haga" },
  { value: "consulted", label: "C · se le consulta" },
  { value: "informed", label: "I · se le informa" },
];
const RACI_ABBR = { responsible: "R", accountable: "A", consulted: "C", informed: "I" };

export default function OrgStructureForm({ canWrite, onChanged }) {
  const [units, setUnits] = useState([]);
  const [people, setPeople] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [completeness, setCompleteness] = useState(null);

  const reload = useCallback(async () => {
    try {
      const [u, p, pr, a, c] = await Promise.all([
        api.get("/org-units"), api.get("/people"), api.get("/processes"),
        api.get("/responsibility-assignments?subject_type=process"),
        api.get("/org-structure/completeness"),
      ]);
      setUnits(u.data); setPeople(p.data); setProcesses(pr.data);
      setAssignments(a.data); setCompleteness(c.data);
      onChanged?.();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  }, [onChanged]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [u, p, pr, a, c] = await Promise.all([
          api.get("/org-units"), api.get("/people"), api.get("/processes"),
          api.get("/responsibility-assignments?subject_type=process"),
          api.get("/org-structure/completeness"),
        ]);
        if (cancelled) return;
        setUnits(u.data); setPeople(p.data); setProcesses(pr.data);
        setAssignments(a.data); setCompleteness(c.data);
      } catch { /* vacío al empezar */ }
    })();
    return () => { cancelled = true; };
  }, []);

  const peopleById = Object.fromEntries(people.map((p) => [p.id, p]));

  return (
    <div data-testid="org-structure-form">
      {completeness && (
        <div className="mb-4 bg-slate-50 border border-slate-200 rounded-md p-3">
          <div className="text-[12px] font-medium text-slate-700 mb-2">¿Está completo este paso?</div>
          <div className="space-y-1">
            {completeness.criteria.map((c) => (
              <div key={c.key} className="flex items-center gap-2 text-[13px]" data-testid={`completeness-${c.key}`}>
                {c.ok ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Circle className="h-4 w-4 text-slate-300" />}
                <span className={c.ok ? "text-slate-700" : "text-slate-500"}>{c.label}</span>
                <span className="text-[11px] text-slate-400">({c.value})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Tabs defaultValue="units">
        <TabsList>
          <TabsTrigger value="units" data-testid="tab-units">Departamentos</TabsTrigger>
          <TabsTrigger value="people" data-testid="tab-people">Personas</TabsTrigger>
          <TabsTrigger value="processes" data-testid="tab-processes">Procesos y responsabilidades</TabsTrigger>
        </TabsList>

        <TabsContent value="units"><UnitsTab canWrite={canWrite} units={units} people={people} reload={reload} /></TabsContent>
        <TabsContent value="people"><PeopleTab canWrite={canWrite} people={people} units={units} reload={reload} /></TabsContent>
        <TabsContent value="processes">
          <ProcessesTab canWrite={canWrite} processes={processes} units={units} people={people}
            peopleById={peopleById} assignments={assignments} reload={reload} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function UnitsTab({ canWrite, units, people, reload }) {
  const [name, setName] = useState("");
  const add = async () => {
    if (!name.trim()) return;
    try { await api.post("/org-units", { name: name.trim() }); setName(""); reload(); toast.success("Departamento añadido."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const setHead = async (id, head_person_id) => {
    try { await api.put(`/org-units/${id}`, { head_person_id: head_person_id === "none" ? null : head_person_id }); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/org-units/${id}`); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="mt-3 space-y-3">
      {canWrite && (
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del departamento (p. ej. IT)" data-testid="unit-name" onKeyDown={(e) => e.key === "Enter" && add()} />
          <Button onClick={add} data-testid="unit-add"><Plus className="h-4 w-4 mr-1" /> Añadir</Button>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {units.length === 0 && <div className="px-4 py-5 text-center text-[13px] text-slate-400">Aún no hay departamentos.</div>}
        {units.map((u) => (
          <div key={u.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`unit-row-${u.id}`}>
            <span className="flex-1 text-[14px] text-slate-800">{u.name}</span>
            <div className="w-56">
              <Select value={u.head_person_id || "none"} onValueChange={(v) => setHead(u.id, v)} disabled={!canWrite}>
                <SelectTrigger data-testid={`unit-head-${u.id}`} className="h-8 text-[13px]"><SelectValue placeholder="Quién responde de este dpto." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin responsable</SelectItem>
                  {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {canWrite && <Button variant="ghost" size="sm" onClick={() => del(u.id)} data-testid={`unit-del-${u.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
          </div>
        ))}
      </div>
      <p className="text-[12px] text-slate-500">Quién responde de cada departamento: elige a la persona responsable.</p>
    </div>
  );
}

function PeopleTab({ canWrite, people, units, reload }) {
  const [form, setForm] = useState({ name: "", job_title: "", email: "", org_unit_id: "none" });
  const add = async () => {
    if (!form.name.trim()) return;
    try {
      await api.post("/people", { name: form.name.trim(), job_title: form.job_title, email: form.email || null, org_unit_id: form.org_unit_id === "none" ? null : form.org_unit_id });
      setForm({ name: "", job_title: "", email: "", org_unit_id: "none" }); reload(); toast.success("Persona añadida.");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/people/${id}`); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };
  const invite = async (id) => {
    try { await api.post(`/people/${id}/invite`, { role: "member" }); toast.success("Invitación enviada."); reload(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const unitName = (id) => units.find((u) => u.id === id)?.name;

  return (
    <div className="mt-3 space-y-3">
      {canWrite && (
        <div className="grid grid-cols-2 gap-2">
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nombre y apellidos" data-testid="person-name" />
          <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} placeholder="Cargo" data-testid="person-title" />
          <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email (opcional)" data-testid="person-email" />
          <div className="flex gap-2">
            <Select value={form.org_unit_id} onValueChange={(v) => setForm({ ...form, org_unit_id: v })}>
              <SelectTrigger data-testid="person-unit"><SelectValue placeholder="Departamento" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin departamento</SelectItem>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button onClick={add} data-testid="person-add"><Plus className="h-4 w-4" /></Button>
          </div>
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-md divide-y divide-slate-100">
        {people.length === 0 && <div className="px-4 py-5 text-center text-[13px] text-slate-400">Aún no hay personas. No hace falta que tengan cuenta en Nimbus.</div>}
        {people.map((p) => (
          <div key={p.id} className="flex items-center gap-3 px-4 py-2.5" data-testid={`person-row-${p.id}`}>
            <div className="flex-1">
              <div className="text-[14px] text-slate-800">{p.name} {p.user_id && <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 ml-1">Tiene cuenta</Badge>}</div>
              <div className="text-[12px] text-slate-500">{p.job_title || "—"}{p.org_unit_id ? ` · ${unitName(p.org_unit_id) || ""}` : ""}{p.email ? ` · ${p.email}` : ""}</div>
            </div>
            {canWrite && p.email && !p.user_id && (
              <Button variant="outline" size="sm" onClick={() => invite(p.id)} data-testid={`person-invite-${p.id}`}><UserPlus className="h-3.5 w-3.5 mr-1" /> Invitar</Button>
            )}
            {canWrite && <Button variant="ghost" size="sm" onClick={() => del(p.id)} data-testid={`person-del-${p.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
          </div>
        ))}
      </div>
    </div>
  );
}

function ProcessesTab({ canWrite, processes, units, people, peopleById, assignments, reload }) {
  const [name, setName] = useState("");
  const addProc = async () => {
    if (!name.trim()) return;
    try { await api.post("/processes", { name: name.trim() }); setName(""); reload(); toast.success("Proceso añadido."); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const delProc = async (id) => { try { await api.delete(`/processes/${id}`); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="mt-3 space-y-3">
      <p className="text-[12px] text-slate-500">R = lo hace · A = responde de que se haga · C = se le consulta · I = se le informa.</p>
      {canWrite && (
        <div className="flex gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nombre del proceso (p. ej. Gestión de copias)" data-testid="process-name" onKeyDown={(e) => e.key === "Enter" && addProc()} />
          <Button onClick={addProc} data-testid="process-add"><Plus className="h-4 w-4 mr-1" /> Añadir</Button>
        </div>
      )}
      <div className="space-y-3">
        {processes.length === 0 && <div className="bg-white border border-slate-200 rounded-md px-4 py-5 text-center text-[13px] text-slate-400">Aún no hay procesos. Lista tus 3 procesos clave.</div>}
        {processes.map((pr) => (
          <ProcessCard key={pr.id} canWrite={canWrite} process={pr} people={people} peopleById={peopleById}
            assignments={assignments.filter((a) => a.subject_id === pr.id)} reload={reload} onDelete={() => delProc(pr.id)} />
        ))}
      </div>
    </div>
  );
}

function ProcessCard({ canWrite, process, people, peopleById, assignments, reload, onDelete }) {
  const [personId, setPersonId] = useState("");
  const [raci, setRaci] = useState("responsible");
  const add = async () => {
    if (!personId) return toast.error("Elige una persona.");
    try {
      await api.post("/responsibility-assignments", { subject_type: "process", subject_id: process.id, person_id: personId, raci });
      setPersonId(""); reload();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const del = async (id) => { try { await api.delete(`/responsibility-assignments/${id}`); reload(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); } };

  return (
    <div className="bg-white border border-slate-200 rounded-md p-4" data-testid={`process-row-${process.id}`}>
      <div className="flex items-center gap-2">
        <span className="flex-1 text-[14px] font-medium text-slate-800">{process.name}</span>
        {canWrite && <Button variant="ghost" size="sm" onClick={onDelete} data-testid={`process-del-${process.id}`}><Trash2 className="h-4 w-4 text-slate-400" /></Button>}
      </div>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {assignments.length === 0 && <span className="text-[12px] text-slate-400">Sin responsabilidades asignadas.</span>}
        {assignments.map((a) => (
          <Badge key={a.id} variant="outline" className="gap-1 text-[12px]" data-testid={`assign-${a.id}`}>
            <span className="font-semibold text-primary">{RACI_ABBR[a.raci]}</span> {peopleById[a.person_id]?.name || "?"}
            {canWrite && <button onClick={() => del(a.id)} className="ml-1 text-slate-400 hover:text-red-500" data-testid={`assign-del-${a.id}`}>×</button>}
          </Badge>
        ))}
      </div>
      {canWrite && (
        <div className="mt-3 flex gap-2">
          <Select value={personId} onValueChange={setPersonId}>
            <SelectTrigger className="h-8 text-[13px]" data-testid={`assign-person-${process.id}`}><SelectValue placeholder="Persona" /></SelectTrigger>
            <SelectContent>{people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={raci} onValueChange={setRaci}>
            <SelectTrigger className="h-8 text-[13px] w-56" data-testid={`assign-raci-${process.id}`}><SelectValue /></SelectTrigger>
            <SelectContent>{RACI.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={add} data-testid={`assign-add-${process.id}`}><Plus className="h-4 w-4" /></Button>
        </div>
      )}
    </div>
  );
}
